'use client';

import { createContext, type ReactNode, useContext } from 'react';

import {
  type AdvertiserMenuItem,
  hasAllScopes,
  hasSomeScopes as hasSomeScopesUtil,
  type Role,
} from '@campaign-manager/core/model';

/**
 * Project settings structure.
 * This is a minimal interface - actual implementations may have more fields.
 */
export interface ProjectSettings {
  project_key?: string;
  advertiser_menu_items?: AdvertiserMenuItem[];
  logo_url?: string;
  terms_url?: string;
  privacy_url?: string;
  landing_page?: {
    illustration_url?: string;
    background_color?: string;
  };
  name_mapping?: {
    advertiser_key?: string | null;
  };
  colors?: {
    primary?: string;
    secondary?: string;
  };
  currency?: string;
  [key: string]: unknown;
}

/**
 * Project access info for multi-project accounts.
 */
export interface ProjectInfo {
  project_key: string;
  company_name?: string;
}

/**
 * User profile structure.
 */
export interface UserProfile {
  role: string;
  email?: string;
  project_id?: string;
  advertiser_id?: string;
  advertisers?: Array<{
    advertiser_id: string;
    company_name: string;
  }>;
  projects?: ProjectInfo[];
  [key: string]: unknown;
}

/**
 * Basic user info from auth.
 * Includes index signature for additional Auth0 properties (sub, picture, etc.)
 */
export interface UserInfo {
  sub?: string;
  name?: string;
  email?: string;
  currentScope?: string;
  advertiserId?: string;
  roles?: string[];
  [key: string]: unknown;
}

/**
 * Layout context value interface.
 */
export interface LayoutContextValue {
  project: ProjectSettings | null;
  user: UserInfo | null;
  profile: UserProfile | null;
  deploymentVersion?: string | null;
}

/**
 * Advertiser info structure for multi-advertiser accounts.
 */
export interface AdvertiserInfo {
  advertiser_id: string;
  company_name: string;
}

/**
 * My account derived value interface.
 */
export interface MyAccountValue {
  project: ProjectSettings | null;
  profile: UserProfile | null;
  advertiser: AdvertiserInfo | null;
  role?: Role;
  hasScope: (scope: string) => boolean;
  hasSomeScopes: (scopes: string[]) => boolean;
  isMyAdvertiser: (advertiserId: string) => boolean;
  hasRetailerRole: boolean;
  hasAdvertiserRole: boolean;
  belongsToMultipleAdvertisers: boolean;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export interface LayoutContextProviderProps {
  children: ReactNode;
  project: ProjectSettings | null;
  user: UserInfo | null;
  profile: UserProfile | null;
  deploymentVersion?: string | null;
}

/**
 * Framework-agnostic layout context provider.
 *
 * Both Next.js and React Router apps can use this provider
 * with their own data loading mechanisms.
 */
export function LayoutContextProvider({
  children,
  project,
  user,
  profile,
  deploymentVersion,
}: LayoutContextProviderProps) {
  const value: LayoutContextValue = {
    project,
    user,
    profile,
    deploymentVersion,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

/**
 * Hook to access layout context (project, user, profile).
 */
export function useLayoutContext(): LayoutContextValue {
  const context = useContext(LayoutContext);

  if (!context) {
    throw new Error(
      'useLayoutContext must be used within a LayoutContextProvider. ' +
        'Ensure your app is wrapped with LayoutContextProvider.',
    );
  }

  return context;
}

/**
 * Hook to get account-related derived values.
 *
 * Provides role checking, scope checking, and advertiser utilities.
 */
export function useMyAccount(): MyAccountValue {
  const { project, profile } = useLayoutContext();

  if (profile) {
    const { role, advertiser_id: myAdvertiserId, advertisers = [] } = profile;

    // Find the current advertiser from the advertisers list
    const advertiser =
      (Array.isArray(advertisers) && advertisers.find((a) => a.advertiser_id === myAdvertiserId)) ||
      null;

    return {
      project,
      profile,
      advertiser,
      role: role as Role,
      hasScope: (scope: string) => hasAllScopes(role as Role, [scope]),
      hasSomeScopes: (scopes: string[]) => hasSomeScopesUtil(role as Role, scopes),
      isMyAdvertiser: (advertiserId: string) => advertiserId === myAdvertiserId,
      hasRetailerRole: role.startsWith('retailer'),
      hasAdvertiserRole: role.startsWith('advertiser'),
      belongsToMultipleAdvertisers: Array.isArray(advertisers) && advertisers.length > 1,
    };
  }

  return {
    project,
    profile: null,
    advertiser: null,
    hasScope: () => false,
    hasSomeScopes: () => false,
    isMyAdvertiser: () => false,
    hasRetailerRole: false,
    hasAdvertiserRole: false,
    belongsToMultipleAdvertisers: false,
  };
}
